import {
  createSlice,
  createAsyncThunk,
  createDraftSafeSelector,
} from '@reduxjs/toolkit';
import { api } from '../../api/api';

// Normalise an axios error into something serialisable for rejectWithValue, so callers can
// branch on the status (409 = deletion not enabled on Loki, 422 = empty/invalid filter).
const errorPayload = (e) => ({
  status: e?.response?.status ?? null,
  detail: e?.response?.data?.detail ?? null,
  message: e?.message ?? 'Request failed',
});

// Stable identity of a delete filter. A preview is only valid for the filter it ran against,
// so the Delete button compares this key against the current filter's.
export const logFilterKey = ({ task_uuids, older_than }) =>
  JSON.stringify({
    task_uuids: [...(task_uuids || [])].sort(),
    older_than: older_than || null,
  });

const withReject = (fn) => async (arg, { rejectWithValue }) => {
  try {
    return await fn(arg);
  } catch (e) {
    return rejectWithValue(errorPayload(e));
  }
};

export const loadLogStorage = createAsyncThunk(
  'logStorage/loadStorage',
  withReject(() => api.admin.logs.storage()),
);

export const loadLogDeleteRequests = createAsyncThunk(
  'logStorage/loadDeleteRequests',
  withReject(() => api.admin.logs.deleteRequests.list()),
);

export const loadLogTasks = createAsyncThunk(
  'logStorage/loadTasks',
  withReject(() => api.admin.logs.tasks()),
);

export const previewLogDelete = createAsyncThunk(
  'logStorage/previewDelete',
  withReject((filter) => api.admin.logs.previewDelete(filter)),
);

export const deleteLogs = createAsyncThunk(
  'logStorage/delete',
  withReject((filter) => api.admin.logs.delete(filter)),
);

export const cancelLogDeleteRequest = createAsyncThunk(
  'logStorage/cancelDeleteRequest',
  withReject((requestId) => api.admin.logs.deleteRequests.cancel(requestId)),
);

const logStorageSlice = createSlice({
  name: 'logStorage',
  initialState: {
    storage: null,
    storageLoading: false,
    storageError: null,
    requests: [],
    requestsLoading: false,
    requestsError: null,
    tasks: [],
    tasksLoading: false,
    tasksError: null,
    // { key, result } — the preview and the filter key it was computed for.
    preview: null,
    previewLoading: false,
    previewError: null,
    deleting: false,
    cancellingId: null,
  },
  reducers: {
    clearLogDeletePreview(state) {
      state.preview = null;
      state.previewError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadLogStorage.pending, (s) => {
        s.storageLoading = true;
      })
      .addCase(loadLogStorage.fulfilled, (s, a) => {
        s.storageLoading = false;
        s.storageError = null;
        s.storage = a.payload;
      })
      .addCase(loadLogStorage.rejected, (s, a) => {
        s.storageLoading = false;
        s.storageError = a.payload || { message: a.error.message };
      })
      .addCase(loadLogDeleteRequests.pending, (s) => {
        s.requestsLoading = true;
      })
      .addCase(loadLogDeleteRequests.fulfilled, (s, a) => {
        s.requestsLoading = false;
        s.requestsError = null;
        s.requests = a.payload || [];
      })
      .addCase(loadLogDeleteRequests.rejected, (s, a) => {
        s.requestsLoading = false;
        s.requestsError = a.payload || { message: a.error.message };
      })
      .addCase(loadLogTasks.pending, (s) => {
        s.tasksLoading = true;
      })
      .addCase(loadLogTasks.fulfilled, (s, a) => {
        s.tasksLoading = false;
        s.tasksError = null;
        s.tasks = a.payload || [];
      })
      .addCase(loadLogTasks.rejected, (s, a) => {
        s.tasksLoading = false;
        s.tasksError = a.payload || { message: a.error.message };
      })
      .addCase(previewLogDelete.pending, (s) => {
        s.previewLoading = true;
        s.previewError = null;
        s.preview = null;
      })
      .addCase(previewLogDelete.fulfilled, (s, a) => {
        s.previewLoading = false;
        s.preview = { key: logFilterKey(a.meta.arg), result: a.payload };
      })
      .addCase(previewLogDelete.rejected, (s, a) => {
        s.previewLoading = false;
        s.previewError = a.payload || { message: a.error.message };
      })
      .addCase(deleteLogs.pending, (s) => {
        s.deleting = true;
      })
      .addCase(deleteLogs.fulfilled, (s) => {
        s.deleting = false;
        // The previewed logs are now queued for deletion; a fresh preview is needed.
        s.preview = null;
      })
      .addCase(deleteLogs.rejected, (s) => {
        s.deleting = false;
      })
      .addCase(cancelLogDeleteRequest.pending, (s, a) => {
        s.cancellingId = a.meta.arg;
      })
      .addCase(cancelLogDeleteRequest.fulfilled, (s, a) => {
        s.cancellingId = null;
        s.requests = s.requests.filter((r) => r.request_id !== a.meta.arg);
      })
      .addCase(cancelLogDeleteRequest.rejected, (s) => {
        s.cancellingId = null;
      });
  },
});

export const { clearLogDeletePreview } = logStorageSlice.actions;

const selectSelf = (state) => state.logStorage;
export const selectLogStorage = createDraftSafeSelector(
  selectSelf,
  (s) => s.storage,
);
export const selectLogStorageLoading = createDraftSafeSelector(
  selectSelf,
  (s) => s.storageLoading,
);
export const selectLogStorageError = createDraftSafeSelector(
  selectSelf,
  (s) => s.storageError,
);
export const selectLogDeleteRequests = createDraftSafeSelector(
  selectSelf,
  (s) => s.requests,
);
export const selectLogDeleteRequestsLoading = createDraftSafeSelector(
  selectSelf,
  (s) => s.requestsLoading,
);
export const selectLogDeleteRequestsError = createDraftSafeSelector(
  selectSelf,
  (s) => s.requestsError,
);
export const selectLogTasks = createDraftSafeSelector(
  selectSelf,
  (s) => s.tasks,
);
export const selectLogTasksLoading = createDraftSafeSelector(
  selectSelf,
  (s) => s.tasksLoading,
);
export const selectLogTasksError = createDraftSafeSelector(
  selectSelf,
  (s) => s.tasksError,
);
export const selectLogDeletePreview = createDraftSafeSelector(
  selectSelf,
  (s) => s.preview,
);
export const selectLogDeletePreviewLoading = createDraftSafeSelector(
  selectSelf,
  (s) => s.previewLoading,
);
export const selectLogDeletePreviewError = createDraftSafeSelector(
  selectSelf,
  (s) => s.previewError,
);
export const selectLogDeleting = createDraftSafeSelector(
  selectSelf,
  (s) => s.deleting,
);
export const selectLogCancellingId = createDraftSafeSelector(
  selectSelf,
  (s) => s.cancellingId,
);

export default logStorageSlice.reducer;
